import type { CauseAnalysisMethod, Issue, IssueMode, IssueStatus, RootCauseAnalysis } from '../types/issue';
import { hypothesisText } from './causeDraft';

export const INVESTIGATION_GATE_MESSAGE = 'Registre ao menos um fato da investigação antes de passar à causa.';
export const CAUSE_GATE_MESSAGE = 'Confirme ao menos uma causa antes de definir o tratamento.';

const LEGACY_STATUSES: IssueStatus[] = ['action_plan', 'execution', 'validation', 'monitoring', 'closed'];

type CauseIssue = Pick<Issue, 'mode' | 'status' | 'root_cause' | 'root_cause_analysis'>;

export function hasInvestigationFact(issue: Pick<Issue, 'root_cause_analysis'>): boolean {
  return Boolean(issue.root_cause_analysis?.problem_statement?.trim());
}

/** Só a confirmação humana conta. Texto solto, 5 Porquês e Ishikawa continuam hipótese. */
export function isCauseConfirmed(issue: Pick<Issue, 'root_cause_analysis'>): boolean {
  const analysis = issue.root_cause_analysis;
  return Boolean(analysis?.cause_confirmed === true && analysis.root_cause_description?.trim());
}

export interface HypothesisItem {
  source: 'five_whys' | 'ishikawa' | 'ai' | 'free';
  text: string;
}

export function listHypotheses(analysis: RootCauseAnalysis | null | undefined): HypothesisItem[] {
  const whys = (analysis?.five_whys ?? [])
    .filter((entry) => entry.answer.trim())
    .map((entry) => ({ source: 'five_whys' as const, text: entry.answer.trim() }));
  const bones = (analysis?.ishikawa ?? []).flatMap((group) => group.causes
    .map((cause) => hypothesisText(cause))
    .filter(Boolean)
    .map((text) => ({ source: 'ishikawa' as const, text })));
  const suggested = (analysis?.suggested_hypotheses ?? [])
    .filter((item) => item.text.trim())
    .map((item) => ({ source: item.source, text: item.text.trim() }));
  return [...whys, ...bones, ...suggested];
}

export function recordAiHypothesis(
  analysis: RootCauseAnalysis,
  text: string,
): RootCauseAnalysis {
  const trimmed = text.trim();
  if (!trimmed) return analysis;
  return {
    ...analysis,
    cause_confirmed: false,
    suggested_hypotheses: [...(analysis.suggested_hypotheses ?? []), { text: trimmed, source: 'ai' }],
  };
}

export function confirmCause(
  analysis: RootCauseAnalysis,
  input: { description: string; evidence: string; method: CauseAnalysisMethod },
): RootCauseAnalysis {
  const description = input.description.trim();
  const evidence = input.evidence.trim();
  if (!description) throw new Error('A causa precisa de uma descrição.');
  if (!evidence) throw new Error('A causa precisa de uma justificativa.');
  return {
    ...analysis,
    root_cause_description: description,
    cause_evidence: evidence,
    analysis_method: input.method,
    cause_confirmed: true,
  };
}

/** Tira a confirmação e preserva o texto e a análise. Não muda o status. */
export function reviseConfirmedCause(analysis: RootCauseAnalysis): {
  previousDescription: string | null;
  analysis: RootCauseAnalysis;
} {
  return {
    previousDescription: analysis.cause_confirmed ? analysis.root_cause_description : null,
    analysis: { ...analysis, cause_confirmed: false },
  };
}

export function structuredCauseGate(issue: CauseIssue, to: IssueStatus): string | null {
  if (issue.mode !== 'structured_problem') return null;
  if (to === 'root_cause' && !hasInvestigationFact(issue)) return INVESTIGATION_GATE_MESSAGE;
  const enteringTreatment = to === 'action_plan'
    && (issue.status === 'investigation' || issue.status === 'root_cause');
  if (enteringTreatment && !isCauseConfirmed(issue)) return CAUSE_GATE_MESSAGE;
  return null;
}

export function requiresStructuredCause(mode: IssueMode): boolean {
  return mode === 'structured_problem';
}

export function isLegacyUnconfirmedCause(issue: CauseIssue): boolean {
  if (issue.mode !== 'structured_problem') return false;
  if (!LEGACY_STATUSES.includes(issue.status)) return false;
  return !isCauseConfirmed(issue);
}

export function causeHistoryMessage(
  previous: RootCauseAnalysis | null | undefined,
  next: RootCauseAnalysis,
): string {
  const was = previous?.cause_confirmed === true;
  const now = next.cause_confirmed === true && Boolean(next.root_cause_description?.trim());
  if (now && was && previous?.root_cause_description?.trim() !== next.root_cause_description.trim()) {
    return 'Causa confirmada revista';
  }
  if (now && !was) return 'Causa confirmada';
  if (!now && was) return 'Causa revista';
  if (hasInvestigationFact({ root_cause_analysis: next }) || listHypotheses(next).length > 0) {
    return 'Investigação registrada';
  }
  return 'Análise de causa atualizada';
}
